import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '../../library/elements/d-wrap';
import '../../library/fields/d-view-info';
import '../../library/editors/elements/d-edit-textarea.js';
import '../../library/editors/elements/d-select-date.js';
import '../../library/elements/d-section.js';
import '../../library/editors/elements/d-select-checkbox.js';
import { BaseDialog } from 'src/library/components/BaseDialog.js';
import type { SelectCheckboxOption } from 'src/library/editors/elements/select-checkbox-option';
import type { TutorialRegistrationDialogInput } from 'src/content/tutorials/tutorial-registration-dialog-input';
import type { TutorialRegistrationDialogResult } from 'src/content/tutorials/tutorial-registration-dialog-result';
import type { DialogActionInput } from 'src/library/components/DialogActionInput';
import type { TutorialParticipant } from 'src/library/lists/tutorial-participant';
import { LocalDate } from 'src/utilities/local-date';
import { t } from 'src/i18n';

/**
 *
 * USAGE:
 *    d-event-view
 *
 */
@customElement('tutorial-registration-dialog')
export class TutorialRegistrationDialog extends BaseDialog<
  TutorialRegistrationDialogInput,
  TutorialRegistrationDialogResult
> {
  static readonly styles = [
    BaseDialog.styles[0],
    css`
      .header-wrapper {
        display: block;
        background: white;
        padding: 0 20px 10px;
        line-height: var(--lineHeightDefault);
        z-index: 2;
      }
      .sub-header {
        margin-bottom: 8px;
      }
      .name {
        font-size: 18px;
        font-weight: 500;
      }
      .version {
        font-size: 16px;
        font-weight: 200;
      }
      .no-flex > * {
        width: 100%;
      }
      d-view-info {
        margin-bottom: 12px;
      }
    `,
  ];
  width = 600;
  @property({ type: String })
  tutorialName = '';
  @property({ type: String })
  comment = '';
  @property({ type: Array })
  employees: TutorialParticipant[] = [];
  @property({ type: Array })
  participants: string[] = [];
  @property({ type: String })
  due = '';

  protected get calculatedTitle(): string {
    return t('tutorials.registrationTitle');
  }

  private get doneDisabled(): boolean {
    return this.participants.length === 0;
  }

  protected get calculatedFooterActions(): DialogActionInput[] {
    return [
      { name: t('content.cancel'), action: 'cancel' },
      { name: t('content.done'), action: 'done', main: true, disabled: this.doneDisabled },
    ];
  }

  private subLabel(employee: TutorialParticipant): string {
    if (employee.hasActiveRegistration) {
      return t('tutorials.alreadyRegistered');
    }
    if (!employee.hasAccess) {
      return t('tutorials.noTrinnvisAccess');
    }
    return '';
  }

  private get employeesOptions(): SelectCheckboxOption[] {
    return this.employees.map((e: TutorialParticipant) => {
      return {
        value: e.uuid || e.employeeUuid || '',
        text: e.name || '',
        disabled: !e.hasAccess || e.hasActiveRegistration,
        sublabel: this.subLabel(e),
      };
    });
  }

  private onParticipantsChanged(e: CustomEvent<{ value: string[] }>) {
    this.participants = e.detail.value;
  }

  private get selectables() {
    return this.employeesOptions.filter((e) => {
      return !e.disabled;
    });
  }

  private onLabelAction() {
    if (this.selectables.length > 0 && this.selectables.length === this.participants.length) {
      this.participants = [];
    } else {
      this.participants = this.selectables.map((e) => {
        return e.value;
      });
    }
  }

  protected renderPostHeader() {
    return html`
      <div class="header-wrapper">
        <d-wrap tight baseline class="sub-header">
          <div class="name">${this.tutorialName}</div>
        </d-wrap>
        <d-wrap class="no-flex">
          <d-view-info content=${t('tutorials.registrationInfo')}> </d-view-info>
          <d-edit-textarea
            placeholder="${t('tutorials.commentPlaceholder')}"
            .value=${this.comment}
            @value-changed=${(e) => (this.comment = e.detail.value)}
          ></d-edit-textarea>
          <d-select-date
            label="${t('tutorials.deadlineLabel')}"
            inline-label
            light-label
            .disableBefore=${LocalDate.now().toString()}
            .value=${this.due}
            @value-changed=${(e) => (this.due = e.detail.value)}
          ></d-select-date>
        </d-wrap>
      </div>
    `;
  }

  renderEmployees() {
    return html` <d-select-checkbox
      checklist
      wrap-label
      italic-sublabel
      disabled-x
      class="fullWidth"
      label="${t('tutorials.selectParticipants')}"
      .labelAction=${this.selectables.length > 0 && this.selectables.length === this.participants.length
        ? t('tutorials.selectNone')
        : t('tutorials.selectAll')}
      .options=${this.employeesOptions}
      label-action-plain
      .value=${this.participants}
      @value-changed=${(e) => this.onParticipantsChanged(e)}
      @label-action=${() => this.onLabelAction()}
    ></d-select-checkbox>`;
  }

  renderBody() {
    return html` <d-section> ${this.renderEmployees()}</d-section> `;
  }

  protected fetchResult(detail: string | undefined): TutorialRegistrationDialogResult {
    if (detail === 'done') {
      return {
        // Only return selected participants, not all employees
        participants: this.employees
          .filter((e: TutorialParticipant) => this.participants.includes(e.uuid || e.employeeUuid || ''))
          .map((e: TutorialParticipant): TutorialParticipant => {
            return {
              ...e,
              assigned: LocalDate.now().toString(),
            };
          }),
        comment: this.comment,
        due: this.due,
      };
    }
    return {
      participants: [],
      comment: '',
      due: '',
    };
  }

  protected initializeDialog(input: TutorialRegistrationDialogInput) {
    this.tutorialName = input.tutorialName;
    this.due = input.due;
    this.employees = input.employees;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tutorial-registration-dialog': TutorialRegistrationDialog;
  }
}
